import { SelectOption } from 'common/components';
import { formatDatetime, formatFileName } from 'common/utils/file';

import { ConfigurationIndicatorVM, ConfigurationTableRow } from '../models/configuration.model';
import { Action } from 'common/enums';
import { VideoLinkStatus, VideoLinkType } from '../enums/configuration.enum';

export const getHierarchyListColDefs = (
    selectedCategory: string,
    handleRadioClickedCallback: (isSelected: boolean, data: any) => void,
    handleExpansionToggleCallback: (data: any) => void,
    minWidth = 450
) => [
    {
        field: 'name',
        headerName: '',
        minWidth: minWidth,
        resizable: true,
        cellRenderer: 'radioButtonCellRenderer',
        cellRendererParams: {
            selectedCategory: selectedCategory,
            onClickRadioButton: handleRadioClickedCallback,
            onExpansionToggle: handleExpansionToggleCallback
        }
    }
];

export const getHierarchyCheckboxListColDefs = (
    onlyIndicator: boolean,
    handleCheckboxClickedCallback: (isSelected: boolean, data: any) => void,
    handleExpansionToggleCallback: (data: any) => void,
    minWidth = 450
) => [
    {
        field: 'name',
        headerName: '',
        minWidth: minWidth,
        resizable: true,
        cellRenderer: 'CheckboxCellRenderer',
        cellRendererParams: {
            onlyIndicator,
            onClickCheckbox: handleCheckboxClickedCallback,
            onExpansionToggle: handleExpansionToggleCallback
        }
    }
];

export const getWhatsNewColDefs = (
    handleActionCallback: (action: string, data: any) => void,
    handleLink: (data: any) => void,
    isSetUpDisabled?: boolean,
    minWidth = 50
) => [
    {
        field: '',
        rowDrag: isSetUpDisabled ? false : true,
        width: 30
    },
    {
        field: 'order',
        headerName: 'S.No',
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'serial-number'
        }
    },
    {
        field: 'name',
        headerName: 'Name',
        minWidth: minWidth,
        width: 600,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'indicator'
        }
    },
    {
        field: 'type',
        headerName: 'Type',
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'type'
        }
    },
    {
        field: 'link',
        headerName: 'Link',
        resizable: true,
        cellRenderer: 'LinkCellRenderer',
        cellRendererParams: {
            onLinkClick: handleLink
        }
    },

    {
        field: 'order',
        headerName: 'Order',
        resizable: true,
        cellRenderer: 'orderCellRenderer'
    },
    {
        field: 'actions',
        headerName: '',
        minWidth: 150,
        resizable: true,
        cellRenderer: 'whatsNewActionCellRenderer',
        cellRendererParams: {
            onAction: handleActionCallback,
            isSetUpDisabled: isSetUpDisabled
        }
    }
];

export const getVideoLinkColDefs = (
    handleActionCallback: (action: string, data: any) => void,
    isSetUpDisabled = false,
    minWidth = 50
) => [
    {
        field: 'order',
        headerName: 'S.No',
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'serial-number'
        }
    },
    {
        field: 'title',
        headerName: 'Title',
        minWidth: minWidth,
        width: 600,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'indicator'
        }
    },
    {
        field: 'videoTypeName',
        headerName: 'Type',
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'type'
        }
    },
    {
        field: 'actions',
        headerName: '',
        minWidth: 150,
        resizable: true,
        cellRenderer: 'whatsNewActionCellRenderer',
        cellRendererParams: {
            onAction: handleActionCallback,
            isSetUpDisabled
        }
    }
];

/**
 * Following function will be called from Configuration Trending Data
 * @param handleRadioClickedCallback
 * @param handleExpansionToggleCallback
 * @returns
 */
export const getTrendingDataListColDefs = (
    data: any,
    onAction: (action: string, data: any) => void,
    handleDeleteCallback: (isSelected: boolean, data: any) => void,
    minWidth = 50
) => [
    {
        field: 'sno',
        headerName: 'S.No',
        minWidth: minWidth,
        width: 100,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'serial-number',
            onClickDelete: handleDeleteCallback
        }
    },
    {
        field: 'indicator',
        headerName: 'Indicator',
        minWidth: minWidth,
        width: 600,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'indicator',
            onClickDelete: handleDeleteCallback
        }
    },
    {
        field: 'indicator_level',
        headerName: 'Level',
        minWidth: minWidth,
        width: 900,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'breadcrum-level',
            onClickDelete: handleDeleteCallback
        }
    },
    {
        field: '',
        headerName: 'Action',
        minWidth: 50,
        resizable: true,
        cellRenderer: 'DeleteIconCellRenderer',
        cellRendererParams: {
            data: data,
            onAction: onAction,
            onClickDelete: handleDeleteCallback
        }
    }
];

export const FREQUENTLY_USED_TITLE = 'Frequently Used';
export const VIDEO_LINK_TITLE = 'Video Link';

export const getConfigurationIndicatorsDataListColDefs = (
    data: ConfigurationIndicatorVM[],
    handleDelete: (data: ConfigurationTableRow) => void,
    isActionDisabled = false,
    minWidth = 50
) => [
    {
        field: '',
        rowDrag: isActionDisabled ? false : true,
        width: 30
    },
    {
        field: 'order',
        headerName: 'S.No',
        minWidth: minWidth,
        width: 120,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'serial-number'
        }
    },
    {
        field: 'name',
        headerName: 'Indicator',
        minWidth: minWidth,
        width: 600,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'indicator'
        }
    },
    {
        field: 'path',
        headerName: 'Level',
        minWidth: minWidth,
        width: 900,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'breadcrum-level'
        }
    },
    {
        field: 'order',
        headerName: 'Order',
        resizable: true,
        cellRenderer: 'orderCellRenderer'
    },
    {
        field: '',
        headerName: 'Action',
        minWidth: 50,
        resizable: true,
        cellRenderer: 'DeleteIconCellRenderer',
        cellRendererParams: {
            data: data,
            onDelete: handleDelete,
            isActionDisabled: isActionDisabled
        }
    }
];

export const GEOGRAPHY_OVERVIEW_TITLE = 'Geography Indicators';

export const VIDEO_LINK_STATUS: SelectOption[] = [
    {
        label: VideoLinkStatus.Active,
        value: VideoLinkStatus.Active
    },
    {
        label: VideoLinkStatus.Inactive,
        value: VideoLinkStatus.Inactive
    }
];

export const VIDEO_LINK_TYPE: SelectOption[] = [
    {
        label: VideoLinkType.Launch_Intro_Video,
        value: VideoLinkType.Launch_Intro_Video
    },
    {
        label: VideoLinkType.Watch_Tutorial,
        value: VideoLinkType.Watch_Tutorial
    }
];

export const VIDEO_LINK_ID_EMPTY_ERROR = 'Video link id cannot be empty';
export const VIDEO_LINK_URL_EMPTY_ERROR = 'Video link url cannot be empty';
export const VIDEO_LINK_ACCOUNT_ID_EMPTY_ERROR = 'Video link account id cannot be empty';
export const VIDEO_LINK_TITLE_MAX_LENGTH = 200;
export const VIDEO_LINK_TITLE_EMPTY_ERROR = 'Video link title cannot be empty';
export const VIDEO_LINK_TITLE_MAX_LENGTH_ERROR = 'Title exceeds 200 characters!';
export const VIDEO_LINK_TITLE_INVALID_CHAR_ERROR = 'Title has invalid characters.';
export const VIDEO_LINK_INVALID_URL_ERROR = 'Data is invalid, please provide valid URL.';
export const VIDEO_LINK_ID_MAX_LENGTH = 50;
export const VIDEO_LINK_ID_MAX_LENGTH_ERROR = 'Id exceeds 50 characters!';
export const VIDEO_LINK_ID_INVALID_CHAR_ERROR = 'Id has invalid characters.';
export const VIDEO_LINK_SHORT_URL_MAX_LENGTH = 100;
export const VIDEO_LINK_SHORT_URL_MAX_LENGTH_ERROR = 'Short url exceeds 100 characters!';
export const VIDEO_LINK_STATUS_EMPTY_ERROR = 'Status cannot be empty';
export const VIDEO_LINK_VIDEO_TYPE_EMPTY_ERROR = 'Video type cannot be empty';
export const VIDEO_LINK_ID_ALREADY_EXIST = 'Video ID already exists';
export const VIDEO_LINK_TYPE_ACTIVE_ALREADY_EXIST =
    'This video type already exist. Do you want to inactivate the existing video type?';
export const VIDEO_LINK_TYPE_ALREADY_EXIST_ACTIVE_STATE =
    'This video type already exist. Do you want to inactivate the existing video type?';
export const VIDEO_LINK_TYPE_INACTIVE_STATE =
    'You are trying to upload an inactive video. Do you want to continue?';
export const VIDEO_LINK_CONFIRMATION_HEADER = 'Video link save confirmation';

export const FILE_UPLOAD_DATA_TITLE = 'Upload History';
export const KEY_INDICATOR_TITLE = 'Key Indicator';
export const STATIC_INDICATOR_TITLE = 'Static Indicator';
export const TRENDING_DATA_TITLE = 'Trending Data';
export const WHATS_NEW_TITLE = "What's New";
export const ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE =
    'Choose where you want to add the new sub category and fill the form on the right';
export const SAVE_CONFIGURATION_FAILED = 'Failed to save the data';
export const SAVE_CONFIGURATION_SUCCESS = 'Data saved successfully';
export const INDICATOR_SOURCES_TITLE = 'Indicator Sources';
export const INDICATOR_SOURCES_GUIDE = `Below listed are the data sources for the indicators. To remove, click on <mark>'Delete'</mark> and to add, click on <mark>'Add new'</mark>.
Here you can make changes to the Data sources, to propagate your changes to external Application, click on <mark>'Map to GBB Public'.</mark>`;

export const SOURCE_NAME_EMPTY_ERROR = 'Source Name cannot be empty';
export const WEB_ADDRESS_EMPTY_ERROR = 'Web Address cannot be empty';
export const SOURCE_NAME_MAX_LENGTH = 75;
export const SOURCE_NAME_MAX_LENGTH_ERROR = 'Source Name exceeds 75 characters!';
export const SOURCE_NAME_INVALID_CHAR_ERROR = 'Source Name has invalid characters';
export const WEB_ADDRESS_MAX_LENGTH = 1000;
export const WEB_ADDRESS_MAX_LENGTH_ERROR = 'Web Address exceeds 1000 characters!';
export const VIDEO_LINK_MAX_ACCOUNT_ID = 50;

export const getUploadFilesColDefs = (minWidth = 50) => [
    {
        field: 'fileId',
        headerName: 'Request#',
        minWidth: minWidth,
        width: 200,
        resizable: true,
        cellRenderer: 'CopyLabelCellRenderer'
    },
    {
        field: 'uploadedUser.displayName',
        headerName: 'Uploaded By',
        minWidth: minWidth,
        width: 200,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellClass: 'name-cell-custom-uploaded-by',
        cellRendererParams: {
            className: 'indicator'
        }
    },
    {
        field: 'uploadedOn',
        headerName: 'Uploaded At',
        minWidth: minWidth,
        width: 250,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellClass: 'name-cell-custom-uploaded-at',
        cellRendererParams: {
            className: 'indicator'
        },
        valueFormatter: (params: any) => formatDatetime(params.value)
    },
    {
        field: 'fileName',
        headerName: 'File Name',
        minWidth: minWidth,
        width: 400,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellClass: 'name-cell-custom-file-name',
        cellRendererParams: {
            className: 'indicator'
        },
        valueFormatter: (params: any) => formatFileName(params.value)
    },
    {
        field: 'statusName',
        headerName: 'Status',
        minWidth: minWidth,
        width: 120,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellClass: 'name-cell-custom-file-status',
        cellRendererParams: {
            className: 'indicator'
        }
    }
];

export const getIndicatorSourcesColDefs = (
    handleAction: (action: Action, data: any) => void,
    isSetUpDisabled = false,
    minWidth = 50
) => [
    {
        field: 'slNo',
        headerName: 'S.No',
        minWidth: minWidth,
        width: 120,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'serial-number'
        }
    },
    {
        field: 'sourceName',
        headerName: 'Source',
        minWidth: minWidth,
        width: 200,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'ellipsis'
        }
    },
    {
        field: 'webAddress',
        headerName: 'Web Address',
        width: 350,
        resizable: true,
        cellRenderer: 'LabelCellRenderer',
        cellRendererParams: {
            className: 'ellipsis'
        }
    },
    {
        field: '',
        headerName: '',
        minWidth: 50,
        resizable: true,
        cellRenderer: 'actionCellRenderer',
        cellRendererParams: {
            onAction: handleAction,
            isSetUpDisabled: isSetUpDisabled
        }
    }
];
